import type { Report, ReportBlock } from './types';

/**
 * One renderer for the screen, the printer and the PDF.
 *
 * Laid out at A4 width in millimetres so what a coach sees is what comes out of
 * the printer. Colours are club colours where the club set one, and ink
 * everywhere else — a report printed on a club's machine in black and white
 * still has to be readable, so nothing carries meaning by colour alone.
 */
export function ReportView({ report }: { report: Report }) {
  const accent = report.meta.accent || '#1f2937';
  const landscape = report.orientation === 'landscape';
  const fit = report.fit === 'page';

  return (
    <article
      className="report-sheet"
      data-orientation={landscape ? 'landscape' : 'portrait'}
      style={{
        // A4 with 14mm margins. mm keeps print and PDF identical.
        width: landscape ? '297mm' : '210mm',
        // A fixed height is what lets the stretching table know what is left.
        // Half a millimetre under the sheet, because a box that measures exactly
        // one page rounds up to a second blank one on some printers.
        ...(fit
          ? { height: landscape ? '209.5mm' : '296.5mm' }
          : { minHeight: landscape ? '210mm' : '297mm' }),
        padding: landscape ? '10mm 12mm' : '14mm',
        background: '#fff',
        color: '#111827',
        boxSizing: 'border-box',
        ...(fit ? { display: 'flex', flexDirection: 'column' } : {}),
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '5mm',
          borderBottom: `1.2mm solid ${accent}`,
          paddingBottom: '4mm',
          marginBottom: '6mm',
        }}
      >
        {report.meta.logoUrl ? (
          <img
            src={report.meta.logoUrl}
            alt=""
            style={{ width: '18mm', height: '18mm', objectFit: 'contain' }}
            crossOrigin="anonymous"
          />
        ) : null}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '3.6mm', letterSpacing: '0.2mm', color: '#6b7280' }}>
            {report.meta.clubName}
          </div>
          <h1 style={{ fontSize: '7mm', margin: '1mm 0 0', lineHeight: 1.15, fontWeight: 700 }}>
            {report.meta.title}
          </h1>
          {report.meta.subtitle ? (
            <div style={{ fontSize: '4mm', marginTop: '1mm', color: '#374151' }}>
              {report.meta.subtitle}
            </div>
          ) : null}
        </div>
        <dl style={{ margin: 0, fontSize: '3.2mm', textAlign: 'end', color: '#6b7280' }}>
          {[
            report.meta.teamName,
            report.meta.seasonLabel,
            report.meta.coachName,
            report.meta.onDate,
          ]
            .filter(Boolean)
            .map((line, i) => (
              <div key={i} style={{ marginTop: i === 0 ? 0 : '1mm' }}>
                {line}
              </div>
            ))}
        </dl>
      </header>

      <div
        style={{
          display: fit ? 'flex' : 'grid',
          flexDirection: 'column',
          gap: fit ? '3mm' : '6mm',
          // The blocks take the space between the header and the footer, which is
          // what any block asking to stretch then measures itself against.
          ...(fit ? { flex: 1, minHeight: 0 } : {}),
        }}
      >
        {report.blocks.map((block, i) => (
          <Block key={i} block={block} accent={accent} fit={fit} />
        ))}
      </div>

      <footer
        style={{
          marginTop: fit ? '3mm' : '6mm',
          paddingTop: '3mm',
          borderTop: '0.3mm solid #d1d5db',
          fontSize: '2.8mm',
          color: '#9ca3af',
          display: 'flex',
          justifyContent: 'space-between',
          gap: '4mm',
        }}
      >
        <span>{report.meta.footer || report.meta.clubName}</span>
        <span>MyPlannerPad</span>
      </footer>
    </article>
  );
}

function BlockTitle({ title, accent }: { title?: string; accent: string }) {
  if (!title) return null;
  return (
    <h2
      style={{
        fontSize: '4.2mm',
        margin: '0 0 2mm',
        fontWeight: 700,
        color: accent,
        textTransform: 'none',
      }}
    >
      {title}
    </h2>
  );
}

function Block({ block, accent, fit }: { block: ReportBlock; accent: string; fit: boolean }) {
  if (block.kind === 'note') {
    return (
      <p style={{ fontSize: '3.2mm', color: '#6b7280', margin: 0, lineHeight: 1.5 }}>
        {block.text}
      </p>
    );
  }

  if (block.kind === 'prose') {
    const paragraphs = block.paragraphs.filter((p): p is string => Boolean(p && p.trim()));
    if (paragraphs.length === 0) return null;
    return (
      <section style={{ breakInside: 'avoid' }}>
        <BlockTitle title={block.title} accent={accent} />
        {paragraphs.map((p, i) => (
          <p key={i} style={{ fontSize: '3.6mm', lineHeight: 1.55, margin: i === 0 ? 0 : '2mm 0 0' }}>
            {p}
          </p>
        ))}
      </section>
    );
  }

  if (block.kind === 'facts') {
    return (
      <section style={{ breakInside: 'avoid' }}>
        <BlockTitle title={block.title} accent={accent} />
        <dl
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(38mm, 1fr))',
            gap: '3mm',
            margin: 0,
          }}
        >
          {block.facts.map((f) => (
            <div key={f.label}>
              <dt style={{ fontSize: '2.9mm', color: '#6b7280' }}>{f.label}</dt>
              <dd style={{ fontSize: '4.2mm', margin: '0.5mm 0 0', fontWeight: 600 }}>{f.value}</dd>
            </div>
          ))}
        </dl>
      </section>
    );
  }

  if (block.kind === 'bars') {
    const peak = Math.max(
      1,
      ...block.bars.map((b) => Math.max(b.planned ?? 0, b.actual ?? 0)),
    );
    return (
      <section style={{ breakInside: 'avoid' }}>
        <BlockTitle title={block.title} accent={accent} />
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2mm', height: '34mm' }}>
          {block.bars.map((b, i) => (
            <div key={i} style={{ flex: 1, textAlign: 'center' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                  gap: '0.8mm',
                  height: '26mm',
                }}
              >
                {/* Planned is an outline because it is an intention; actual is
                    solid because it happened. The same language as the app. */}
                <div
                  style={{
                    width: '3mm',
                    height: `${((b.planned ?? 0) / peak) * 100}%`,
                    border: `0.3mm dashed ${accent}`,
                  }}
                />
                <div
                  style={{
                    width: '3mm',
                    height: `${((b.actual ?? 0) / peak) * 100}%`,
                    background: accent,
                  }}
                />
              </div>
              <div style={{ fontSize: '2.6mm', color: '#6b7280', marginTop: '1mm' }}>{b.label}</div>
              {b.mark ? (
                <div style={{ fontSize: '2.6mm', fontWeight: 700 }}>{b.mark}</div>
              ) : null}
            </div>
          ))}
        </div>
        <div style={{ fontSize: '2.8mm', color: '#6b7280', marginTop: '2mm' }}>
          {block.plannedLabel} · {block.actualLabel}
        </div>
      </section>
    );
  }

  const columns = block.columns;
  const grid = block.grid === true;
  // Stretching means the rows divide the page rather than each claiming a height,
  // so the cells give up their own padding and let the table's height set it.
  // Without that a thirty-player register is already two pages before it starts.
  const stretch = fit && block.stretch === true;
  return (
    <section
      style={{
        breakInside: 'auto',
        ...(block.breakBefore ? { breakBefore: 'page' as const } : {}),
        ...(stretch ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : {}),
      }}
    >
      <BlockTitle title={block.title} accent={accent} />
      {block.rows.length === 0 ? (
        <p style={{ fontSize: '3.2mm', color: '#9ca3af', margin: 0 }}>{block.empty ?? '—'}</p>
      ) : (
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: stretch ? '2.9mm' : '3.2mm',
            // Stated widths are honoured and the rest share what is left
            // equally, instead of a long heading taking room from a short one.
            ...(block.evenColumns ? { tableLayout: 'fixed' as const } : {}),
            ...(stretch ? { flex: 1, height: '100%' } : {}),
          }}
        >
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={{
                    textAlign: col.headerAlign ?? col.align ?? 'start',
                    verticalAlign: 'middle',
                    width: col.width,
                    padding: grid ? '1mm 0.6mm' : '1.4mm 1mm',
                    borderBottom: `0.4mm solid ${accent}`,
                    ...(grid ? { border: '0.2mm solid #9ca3af', borderBottom: `0.4mm solid ${accent}` } : {}),
                    fontWeight: 600,
                    color: '#374151',
                    // "12\nMo" — the day over its weekday, in one cell.
                    whiteSpace: 'pre-line',
                    lineHeight: 1.1,
                    fontSize: block.headerFont ?? (grid ? '2.6mm' : undefined),
                    // A heading wraps between its words and never inside one.
                    // "Countermovement" split across two lines reads as a
                    // printing fault, and the column is sized to hold the word
                    // precisely so it never has to be.
                    overflowWrap: 'normal',
                    wordBreak: 'normal',
                    hyphens: 'none',
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, i) => (
              <tr key={i} style={{ breakInside: 'avoid' }}>
                {columns.map((col) => (
                  <td
                    key={col.key}
                    style={{
                      textAlign: col.align ?? 'start',
                      // A cell aligns on its baseline unless told otherwise, which
                      // looks right while a row is the height of its own text and
                      // puts every name against the rule above it the moment the
                      // row is taller than that.
                      verticalAlign: block.rowHeight ? 'top' : 'middle',
                      ...(block.rowHeight ? { height: block.rowHeight } : {}),
                      // A height on a cell is a floor, never a ceiling, so a row
                      // asking to be 6mm tall still came out 8mm while the grid's
                      // own padding held it open. Rows with a stated height give
                      // their padding up and let the number govern.
                      padding: block.rowHeight
                        ? '0.8mm 1mm'
                        : stretch
                          ? '0.4mm 0.6mm'
                          : grid
                            ? '2.2mm 0.6mm'
                            : '1.3mm 1mm',
                      lineHeight: stretch ? 1.05 : undefined,
                      borderBottom: grid ? '0.2mm solid #9ca3af' : '0.2mm solid #e5e7eb',
                      ...(grid ? { border: '0.2mm solid #9ca3af' } : {}),
                    }}
                  >
                    {/* An empty cell in a register is a box to write in, not a
                        missing value, so it stays blank rather than showing a dash. */}
                    {grid ? (row[col.key] ?? '') : (row[col.key] ?? '—')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
